import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { formatDisplayDate } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, X, Upload, Download, FileSpreadsheet, History } from 'lucide-react';
import LifecycleModal from '@/components/LifecycleModal';

export default function SIMConnectionsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [simConnections, setSimConnections] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSim, setEditingSim] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);
  const [lifecycleOpen, setLifecycleOpen] = useState(false);
  const [lifecycleSimId, setLifecycleSimId] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() =>
    location.state?.openSimId || location.state?.openSim ? 1000 : 20
  );
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState({
    mobile_number: '',
    sim_type: '',
    status: '',
    sim_provider: '',
  });
  const [formData, setFormData] = useState({
    sim_provider: '',
    mobile_number: '',
    sim_type: 'Physical SIM',
    status: 'Available',
    employee_id: '',
    asset_id: '',
    assign_date: '',
    purpose: '',
    sim_ownership: 'With Employee',
    return_date: '',
    remarks: '',
  });

  // Asset picker popover state (for asset field in Add/Edit SIM Connection)
  const [assetPopoverOpen, setAssetPopoverOpen] = useState(false);
  const [selectedAssetPickerCategory, setSelectedAssetPickerCategory] = useState('');

  const fetchSIMConnections = useCallback(async () => {
    try {
      const params = {
        page,
        pageSize,
      };
      // Use a single backend "search" term for number / provider / related fields
      if (filters.mobile_number && filters.mobile_number.trim() !== '') {
        params.search = filters.mobile_number.trim();
      }

      const response = await api.get('/sim-connections', { params });
      const res = response.data;
      if (res && typeof res === 'object' && 'data' in res) {
        setSimConnections(res.data || []);
        setTotalCount(res.total ?? 0);
      } else {
        setSimConnections(Array.isArray(res) ? res : []);
        setTotalCount(Array.isArray(res) ? res.length : 0);
      }
    } catch (error) {
      toast.error('Failed to fetch SIM connections');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filters.mobile_number]);

  const fetchEmployees = useCallback(async () => {
    try {
      const response = await api.get('/employees');
      setEmployees(response.data);
    } catch (error) {
      toast.error('Failed to fetch employees');
    }
  }, []);

  const fetchAssets = useCallback(async () => {
    try {
      const response = await api.get('/assets');
      setAssets(response.data);
    } catch (error) {
      toast.error('Failed to fetch assets');
    }
  }, []);

  useEffect(() => {
    setPage(1);
  }, [pageSize, filters.mobile_number]);

  useEffect(() => {
    fetchSIMConnections();
    fetchEmployees();
    fetchAssets();
  }, [fetchSIMConnections, fetchEmployees, fetchAssets]);

  // Get unique values for filters
  const uniqueProviders = [...new Set(simConnections.map(sim => sim.sim_provider).filter(Boolean))].sort();
  const uniqueSimTypes = ['Physical SIM', 'eSIM', 'WhatsApp Only', 'Other'];

  // Selected asset details for display below picker
  const selectedAssetDetails = assets.find((a) => a.asset_id === formData.asset_id);

  // SIM connections are already filtered server-side by the main search term.
  // Remaining dropdown filters (type/status/provider) are applied client-side.
  const filteredSims = simConnections.filter((sim) => {
    const matchesSimType = !filters.sim_type || filters.sim_type === 'all' || sim.sim_type === filters.sim_type;
    const matchesStatus = !filters.status || filters.status === 'all' || sim.status === filters.status;
    const matchesProvider = !filters.sim_provider || filters.sim_provider === 'all' || sim.sim_provider === filters.sim_provider;

    return matchesSimType && matchesStatus && matchesProvider;
  });

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
  };

  const clearFilters = () => {
    setFilters({
      mobile_number: '',
      sim_type: '',
      status: '',
      sim_provider: '',
    });
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const showPagination = totalCount > 20;

  const hasActiveFilters = filters.mobile_number || 
    (filters.sim_type && filters.sim_type !== 'all') || 
    (filters.status && filters.status !== 'all') ||
    (filters.sim_provider && filters.sim_provider !== 'all');

  const handleOpenDialog = (sim = null) => {
    if (sim) {
      setEditingSim(sim);
      // Format assign_date for HTML date input (YYYY-MM-DD format)
      const assignDateVal = sim.assign_date;
      const assignDateStr = assignDateVal ? String(assignDateVal).slice(0, 10) : '';
      
      const returnDateVal = sim.return_date;
      const returnDateStr = returnDateVal ? String(returnDateVal).slice(0, 10) : '';
      setFormData({
        sim_provider: sim.sim_provider,
        mobile_number: sim.mobile_number,
        sim_type: sim.sim_type,
        status: sim.status,
        employee_id: sim.employee_id || '',
        asset_id: sim.asset_id || '',
        assign_date: assignDateStr,
        purpose: sim.purpose || '',
        sim_ownership: sim.sim_ownership,
        return_date: returnDateStr,
        remarks: sim.remarks || '',
      });
    } else {
      setEditingSim(null);
      setFormData({
        sim_provider: '',
        mobile_number: '',
        sim_type: 'Physical SIM',
        status: 'Available',
        employee_id: '',
        asset_id: '',
        assign_date: '',
        purpose: '',
        sim_ownership: 'With Employee',
        return_date: '',
        remarks: '',
      });
    }
    setDialogOpen(true);
  };

  // Open SIM details when navigated from global search or dashboard
  useEffect(() => {
    const openSim = location.state?.openSim;
    const openSimId = location.state?.openSimId;

    // If full SIM object is passed from global search, use it directly
    if (openSim) {
      handleOpenDialog({
        id: openSim.id ?? openSim.sim_connection_id,
        sim_provider: openSim.sim_provider,
        mobile_number: openSim.mobile_number,
        sim_type: openSim.sim_type,
        status: openSim.status,
        employee_id: openSim.employee?.employee_id ?? openSim.employee_id ?? '',
        asset_id: openSim.asset?.asset_id ?? openSim.asset_id ?? '',
        assign_date: openSim.assign_date ?? '',
        purpose: openSim.purpose ?? '',
        sim_ownership: openSim.sim_ownership ?? 'With Employee',
        return_date: openSim.return_date ?? '',
        remarks: openSim.remarks ?? '',
      });
      navigate(location.pathname, { replace: true, state: {} });
      return;
    }

    if (openSimId == null) return;

    const openFromBackend = async () => {
      try {
        const response = await api.get(`/sim-connections/${openSimId}`);
        if (response?.data) {
          handleOpenDialog(response.data);
        }
      } catch (error) {
        console.error('Failed to open SIM from navigation state:', error);
      } finally {
        navigate(location.pathname, { replace: true, state: {} });
      }
    };

    openFromBackend();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- openSim/openSimId from navigation, run once
  }, [location.state?.openSim, location.state?.openSimId, location.pathname, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.sim_provider || !formData.mobile_number || !formData.sim_type || !formData.status || !formData.sim_ownership) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Validate status-specific fields
    if (formData.status === 'Assigned') {
      if (!formData.employee_id) {
        toast.error('Employee ID is required when status is "Assigned"');
        return;
      }
      if (!formData.assign_date) {
        toast.error('Assign date is required when status is "Assigned"');
        return;
      }
    }

    try {
      const submitData = { ...formData };
      
      // Clear assignment fields if status is not Assigned
      if (submitData.status !== 'Assigned') {
        submitData.employee_id = '';
        submitData.asset_id = '';
        submitData.assign_date = '';
      } else if (!submitData.asset_id) {
        submitData.asset_id = null;
      }

      // Include return fields when returning (Assigned -> Available)
      if (editingSim?.status === 'Assigned' && submitData.status === 'Available') {
        submitData.return_date = formData.return_date || new Date().toISOString().split('T')[0];
        submitData.remarks = formData.remarks || null;
      }

      if (editingSim) {
        await api.put(`/sim-connections/${editingSim.id}`, submitData);
        toast.success('SIM connection updated successfully');
      } else {
        await api.post('/sim-connections', submitData);
        toast.success('SIM connection added successfully');
      }
      setDialogOpen(false);
      fetchSIMConnections();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (sim) => {
    if (!window.confirm('Are you sure you want to delete this SIM connection?')) return;

    try {
      await api.delete(`/sim-connections/${sim.id}`);
      toast.success('SIM connection deleted successfully');
      fetchSIMConnections();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to delete SIM connection';
      toast.error(message);
    }
  };

  const handleExport = async () => {
    try {
      const response = await api.get('/sim-connections/export', {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'sim_connections.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('SIM connections exported successfully');
    } catch (error) {
      toast.error('Failed to export SIM connections');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get('/sim-connections/template', {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'sim_connections_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Template downloaded');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/sim-connections/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      // Check if there are errors in the response
      if (response.data.success === false || (response.data.errors && response.data.errors.length > 0)) {
        // Show summary
        toast.error(response.data.summary || response.data.message || 'Import failed');
        
        // Show detailed errors
        if (response.data.errors && response.data.errors.length > 0) {
          // Show first few errors in toast, then all errors in console
          const errorMessages = response.data.errors.slice(0, 3).join('\n');
          const remainingErrors = response.data.errors.length - 3;
          
          if (remainingErrors > 0) {
            toast.error(`${errorMessages}\n... and ${remainingErrors} more error(s). Check console for full details.`, {
              duration: 10000,
            });
          } else {
            toast.error(errorMessages, {
              duration: 10000,
            });
          }
          
          // Log all errors to console for debugging
          console.error('Import Errors:', response.data.errors);
        }
      } else {
        // Success case
        toast.success(response.data.message || response.data.summary || 'SIM connections imported successfully');
      }
      
      fetchSIMConnections();
    } catch (error) {
      // Handle error response
      const errorData = error.response?.data;
      
      if (errorData) {
        // Show summary or message
        const errorMessage = errorData.summary || errorData.message || errorData.detail || 'Failed to import SIM connections';
        toast.error(errorMessage);
        
        // Show detailed errors if available
        if (errorData.errors && errorData.errors.length > 0) {
          const errorMessages = errorData.errors.slice(0, 3).join('\n');
          const remainingErrors = errorData.errors.length - 3;
          
          if (remainingErrors > 0) {
            toast.error(`${errorMessages}\n... and ${remainingErrors} more error(s). Check console for full details.`, {
              duration: 10000,
            });
          } else {
            toast.error(errorMessages, {
              duration: 10000,
            });
          }
          
          // Log all errors to console
          console.error('Import Errors:', errorData.errors);
        }
      } else {
        toast.error(error.message || 'Failed to import SIM connections');
      }
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6 sm:mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0B1F3A]">SIM Connections</h1>
          <p className="text-slate-600 mt-2 text-sm sm:text-base">Manage SIM card connections</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={handleImport}
            className="hidden"
          />
          <Button
            variant="outline"
            onClick={handleDownloadTemplate}
            className="border-slate-300 text-xs sm:text-sm"
            size="sm"
          >
            <FileSpreadsheet className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2" />
            <span className="hidden sm:inline">Template</span>
          </Button>
          <Button
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
            className="border-slate-300 text-xs sm:text-sm"
            size="sm"
          >
            <Upload className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2" />
            <span className="hidden sm:inline">{importing ? 'Importing...' : 'Import'}</span>
            <span className="sm:hidden">{importing ? '...' : 'Imp'}</span>
          </Button>
          <Button
            variant="outline"
            onClick={handleExport}
            className="border-slate-300 text-xs sm:text-sm"
            size="sm"
          >
            <Download className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2" />
            <span className="hidden sm:inline">Export</span>
          </Button>
          <Button onClick={() => handleOpenDialog()} className="bg-[#0B1F3A] hover:bg-[#0B1F3A]/90 text-xs sm:text-sm" size="sm">
            <Plus className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2" />
            <span className="hidden sm:inline">Add SIM Connection</span>
            <span className="sm:hidden">Add</span>
          </Button>
        </div>
      </div>

      {/* Filters Section */}
      <div className="bg-white rounded-lg border border-border p-3 mb-4">
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <div className="flex items-center gap-2 w-full sm:min-w-[200px] sm:flex-1">
            <Input
              id="filter-mobile-number"
              placeholder="Search by mobile number or provider..."
              value={filters.mobile_number}
              onChange={(e) => handleFilterChange('mobile_number', e.target.value)}
              className="h-9 text-sm"
            />
          </div>
          <Select
            value={filters.sim_provider || 'all'}
            onValueChange={(value) => handleFilterChange('sim_provider', value === 'all' ? '' : value)}
          >
            <SelectTrigger id="filter-provider" className="h-9 w-full sm:w-[160px] text-sm">
              <SelectValue placeholder="Provider" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Providers</SelectItem>
              {uniqueProviders.map((provider) => (
                <SelectItem key={provider} value={provider}>
                  {provider}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={filters.sim_type || 'all'}
            onValueChange={(value) => handleFilterChange('sim_type', value === 'all' ? '' : value)}
          >
            <SelectTrigger id="filter-sim-type" className="h-9 w-full sm:w-[160px] text-sm">
              <SelectValue placeholder="SIM Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {uniqueSimTypes.map((type) => (
                <SelectItem key={type} value={type}>
                  {type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={filters.status || 'all'}
            onValueChange={(value) => handleFilterChange('status', value === 'all' ? '' : value)}
          >
            <SelectTrigger id="filter-status" className="h-9 w-full sm:w-[140px] text-sm">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="Available">Available</SelectItem>
              <SelectItem value="Assigned">Assigned</SelectItem>
              <SelectItem value="Inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
          {hasActiveFilters && (
            <>
              <div className="flex items-center gap-2 text-sm text-muted-foreground ml-auto">
                <span>{filteredSims.length} of {simConnections.length}</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="h-9 px-3"
              >
                <X className="h-4 w-4 mr-1" />
                Clear
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-border overflow-hidden">
        <div className="overflow-x-auto -mx-4 sm:mx-0">
          <div className="inline-block min-w-full align-middle">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider whitespace-nowrap">Mobile Number</th>
                  <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider whitespace-nowrap">Provider</th>
                  <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider whitespace-nowrap">SIM Type</th>
                  <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider whitespace-nowrap">Status</th>
                  <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider whitespace-nowrap">Employee</th>
                  <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider whitespace-nowrap">Asset</th>
                  <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider whitespace-nowrap">Assign Date</th>
                  <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider whitespace-nowrap">Ownership</th>
                  <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider whitespace-nowrap">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {filteredSims.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="px-3 sm:px-6 py-8 text-center text-xs sm:text-sm text-muted-foreground">
                      {hasActiveFilters ? 'No SIM connections match the selected filters' : 'No SIM connections found'}
                    </td>
                  </tr>
                ) : (
                  filteredSims.map((sim) => (
                    <tr key={sim.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm font-medium whitespace-nowrap">{sim.mobile_number}</td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm break-words">{sim.sim_provider}</td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm whitespace-nowrap">
                        <span className="px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800">
                          {sim.sim_type === 'Physical' ? 'Physical SIM' : sim.sim_type}
                        </span>
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm whitespace-nowrap">
                        <span
                          className={`px-2 py-1 rounded-md text-xs font-medium ${
                            sim.status === 'Available'
                              ? 'bg-green-100 text-green-800'
                              : sim.status === 'Assigned'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {sim.status}
                        </span>
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm">
                        {sim.employee_name ? (
                          <div>
                            <div className="font-medium break-words">{sim.employee_name}</div>
                            <div className="text-xs text-slate-500 break-words">{sim.employee_id}</div>
                          </div>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm">
                        {sim.asset_name ? (
                          <div>
                            <div className="font-medium break-words">{sim.asset_name}</div>
                            <div className="text-xs text-slate-500 break-words">{sim.asset_id}</div>
                          </div>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm whitespace-nowrap">
                        {sim.assign_date ? formatDisplayDate(sim.assign_date) : '-'}
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm whitespace-nowrap">
                        <span className="px-2 py-1 rounded-md text-xs font-medium bg-purple-100 text-purple-800">
                          {sim.sim_ownership}
                        </span>
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm whitespace-nowrap">
                        <div className="flex gap-1 sm:gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setLifecycleSimId(sim.id);
                              setLifecycleOpen(true);
                            }}
                            title="View Assignment Lifecycle"
                            className="h-8 w-8 p-0"
                          >
                            <History className="h-3 w-3 sm:h-4 sm:w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenDialog(sim)}
                            className="h-8 w-8 p-0"
                          >
                            <Edit className="h-3 w-3 sm:h-4 sm:w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(sim)}
                            className="h-8 w-8 p-0"
                          >
                            <Trash2 className="h-3 w-3 sm:h-4 sm:w-4 text-destructive" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div className="px-4 sm:px-6 py-3 sm:py-4 bg-muted/30 border-t border-border">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
            {showPagination ? (
              <>
                <p className="text-xs sm:text-sm text-muted-foreground flex items-center gap-2 flex-wrap">
                  Show{' '}
                  <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
                    <SelectTrigger className="h-8 w-[70px] text-xs sm:text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="20">20</SelectItem>
                      <SelectItem value="40">40</SelectItem>
                      <SelectItem value="60">60</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                  {' '}| Total Count: <span className="font-semibold text-[#0B1F3A]">{totalCount}</span>
                </p>
                <div className="flex items-center gap-1 text-xs sm:text-sm">
                  <Button variant="outline" size="sm" className="h-8 px-2" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                    &laquo; Previous
                  </Button>
                  <span className="px-2 font-medium text-[#0B1F3A]">{page}/{totalPages}</span>
                  <Button variant="outline" size="sm" className="h-8 px-2" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
                    Next &raquo;
                  </Button>
                </div>
              </>
            ) : (
              <p className="text-xs sm:text-sm font-semibold text-[#0B1F3A]">
                Total: {totalCount} SIM connection{totalCount !== 1 ? 's' : ''}
              </p>
            )}
          </div>
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto mx-4">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl">{editingSim ? 'Edit SIM Connection' : 'Add SIM Connection'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="sim_provider">SIM Provider <span className="text-red-500">*</span></Label>
                <Input
                  id="sim_provider"
                  value={formData.sim_provider}
                  onChange={(e) => setFormData({ ...formData, sim_provider: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="mobile_number">Mobile Number <span className="text-red-500">*</span></Label>
                <Input
                  id="mobile_number"
                  value={formData.mobile_number}
                  onChange={(e) => setFormData({ ...formData, mobile_number: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sim_type">SIM Type <span className="text-red-500">*</span></Label>
                <Select
                  value={formData.sim_type}
                  onValueChange={(value) => setFormData({ ...formData, sim_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Physical SIM">Physical SIM</SelectItem>
                    <SelectItem value="eSIM">eSIM</SelectItem>
                    <SelectItem value="WhatsApp Only">WhatsApp Only</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="status">Status <span className="text-red-500">*</span></Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => {
                    const newFormData = { ...formData, status: value };
                    // Clear assignment fields if status is not Assigned
                    if (value !== 'Assigned') {
                      newFormData.employee_id = '';
                      newFormData.asset_id = '';
                      newFormData.assign_date = '';
                    }
                    setFormData(newFormData);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Available">Available</SelectItem>
                    <SelectItem value="Assigned">Assigned</SelectItem>
                    <SelectItem value="Inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="sim_ownership">SIM Ownership <span className="text-red-500">*</span></Label>
                <Select
                  value={formData.sim_ownership}
                  onValueChange={(value) => setFormData({ ...formData, sim_ownership: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="With Employee">With Employee</SelectItem>
                    <SelectItem value="With Office/HR">With Office/HR</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {formData.status === 'Assigned' && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="employee_id">Employee <span className="text-red-500">*</span></Label>
                    <Select
                      value={formData.employee_id}
                      onValueChange={(value) => setFormData({ ...formData, employee_id: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select employee" />
                      </SelectTrigger>
                      <SelectContent position="popper" sideOffset={5}>
                        {employees
                          .filter(emp => emp.status === 'Active' || (editingSim && formData.employee_id === emp.employee_id))
                          .map((emp) => (
                            <SelectItem key={emp.employee_id} value={emp.employee_id}>
                              {emp.full_name} ({emp.employee_id})
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="assign_date">Assign Date <span className="text-red-500">*</span></Label>
                    <Input
                      id="assign_date"
                      type="date"
                      value={formData.assign_date}
                      onChange={(e) => setFormData({ ...formData, assign_date: e.target.value })}
                      required={formData.status === 'Assigned'}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="asset_id">Asset (Optional)</Label>
                    <Popover open={assetPopoverOpen} onOpenChange={setAssetPopoverOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          className="w-full justify-between"
                        >
                          <span className="truncate text-left">
                            {formData.asset_id
                              ? selectedAssetDetails?.asset_name
                                ? `${selectedAssetDetails.asset_name} (${selectedAssetDetails.asset_id})`
                                : formData.asset_id
                              : 'Select category and asset (optional)'}
                          </span>
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[520px] p-0" align="start">
                        <div className="text-xs sm:text-sm text-slate-600 px-3 py-2 border-b">
                          First choose an asset category on the left, then pick an asset on the right.
                        </div>
                        <div className="flex max-h-72">
                          {/* Categories column */}
                          <div className="w-1/3 border-r border-slate-200 bg-slate-50 overflow-y-auto">
                            {Array.from(
                              new Set(
                                assets
                                  .filter(
                                    (asset) =>
                                      asset.category &&
                                      asset.category.toLowerCase() === 'mobile'
                                  )
                                  .map((asset) => asset.category)
                                  .filter(Boolean)
                              )
                            ).map((category) => (
                              <button
                                key={category}
                                type="button"
                                className={`w-full text-left px-3 py-2 text-xs sm:text-sm border-b last:border-b-0 transition-colors ${
                                  selectedAssetPickerCategory === category
                                    ? 'bg-[#0B1F3A] text-white'
                                    : 'hover:bg-slate-100'
                                }`}
                                onClick={() => {
                                  setSelectedAssetPickerCategory(category);
                                  setFormData((prev) => ({ ...prev, asset_id: '' }));
                                }}
                              >
                                {category}
                              </button>
                            ))}
                            {assets.filter(
                              (asset) =>
                                asset.category &&
                                asset.category.toLowerCase() === 'mobile'
                            ).length === 0 && (
                              <div className="px-3 py-2 text-xs text-slate-500">
                                No mobile assets available
                              </div>
                            )}
                          </div>

                          {/* Assets list column */}
                          <div className="w-2/3 bg-white overflow-y-auto">
                            {assets
                              .filter(
                                (asset) =>
                                  asset.category &&
                                  asset.category.toLowerCase() === 'mobile' &&
                                  (!selectedAssetPickerCategory ||
                                    asset.category === selectedAssetPickerCategory)
                              )
                              .map((asset) => (
                                <button
                                  key={asset.asset_id}
                                  type="button"
                                  className="w-full text-left px-3 py-2 text-xs sm:text-sm border-b last:border-b-0 flex flex-col hover:bg-slate-50 transition-colors"
                                  onClick={() => {
                                    setFormData((prev) => ({
                                      ...prev,
                                      asset_id: asset.asset_id,
                                    }));
                                    setAssetPopoverOpen(false);
                                  }}
                                >
                                  <span className="font-medium">
                                    {asset.brand ? `${asset.brand} - ` : ''}
                                    {asset.asset_name}
                                  </span>
                                  <span className="text-[11px] text-slate-500">
                                    {asset.asset_id} • {asset.category} • {asset.status}
                                  </span>
                                </button>
                              ))}
                            {assets.filter(
                              (asset) =>
                                asset.category &&
                                asset.category.toLowerCase() === 'mobile' &&
                                (!selectedAssetPickerCategory ||
                                  asset.category === selectedAssetPickerCategory)
                            ).length === 0 && (
                              <div className="px-3 py-2 text-xs text-slate-500">
                                {selectedAssetPickerCategory
                                  ? 'No assets in this category'
                                  : 'Select a category to see assets'}
                              </div>
                            )}
                          </div>
                        </div>
                      </PopoverContent>
                    </Popover>
                    {selectedAssetDetails && (
                      <div className="mt-1 text-[11px] text-slate-700 space-y-0.5">
                        <p>
                          <span className="font-semibold">Name:</span>{' '}
                          <span className="font-medium">{selectedAssetDetails.asset_name}</span>
                        </p>
                        <p>
                          <span className="font-semibold">ID:</span>{' '}
                          {selectedAssetDetails.asset_id}
                          {selectedAssetDetails.brand && (
                            <>
                              {'  '}· <span className="font-semibold">Brand:</span>{' '}
                              {selectedAssetDetails.brand}
                            </>
                          )}
                        </p>
                        <p>
                          <span className="font-semibold">Category:</span>{' '}
                          {selectedAssetDetails.category || '-'}{'  '}·{' '}
                          <span className="font-semibold">Status:</span>{' '}
                          {selectedAssetDetails.status}
                        </p>
                      </div>
                    )}
                  </div>
                </>
              )}
              <div className="space-y-2 col-span-2">
                <Label htmlFor="purpose">Remarks/Purpose</Label>
                <Textarea
                  id="purpose"
                  value={formData.purpose}
                  onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
                  placeholder="Enter purpose or notes"
                  rows={3}
                />
              </div>
              {editingSim?.status === 'Assigned' && (
                <>
                  <div className="col-span-2 border-t pt-4 mt-2">
                    <p className="text-sm font-medium text-slate-700 mb-3">Return Details (when changing status to Available)</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="return_date">Return Date</Label>
                    <Input
                      id="return_date"
                      type="date"
                      value={formData.return_date}
                      onChange={(e) => setFormData({ ...formData, return_date: e.target.value })}
                      placeholder="Return date"
                    />
                  </div>
                  <div className="space-y-2 col-span-2">
                    <Label htmlFor="remarks">Remarks (Optional)</Label>
                    <Textarea
                      id="remarks"
                      value={formData.remarks}
                      onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                      placeholder="Enter return remarks or notes"
                      rows={2}
                    />
                  </div>
                </>
              )}
            </div>
            <DialogFooter>
              <Button type="submit">
                {editingSim ? 'Update' : 'Add'} SIM Connection
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <LifecycleModal
        open={lifecycleOpen}
        onOpenChange={setLifecycleOpen}
        type="sim"
        identifier={lifecycleSimId}
      />
    </div>
  );
}
